import { useState, useEffect, useCallback } from 'react';
import { admin } from '../api/client';

function StatCard({ label, value, sub, color }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-value" style={{ color: color || 'var(--text-primary)' }}>{value}</div>
      <div className="admin-stat-label">{label}</div>
      {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
  );
}

function OnlineDot() {
  return <span className="admin-online-dot" />;
}

function timeAgo(dateStr) {
  if (!dateStr) return 'jamais';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'maintenant';
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

function RegistrationChart({ data }) {
  if (!data || data.length === 0) return <div className="admin-chart-empty">Aucune inscription ce mois</div>;
  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="admin-chart">
      <div className="admin-chart-bars">
        {data.map(d => (
          <div key={d.date} className="admin-chart-bar-wrapper" title={`${d.date}: ${d.count} inscription(s)`}>
            <div className="admin-chart-bar" style={{ height: `${(d.count / maxCount) * 100}%` }}>
              {d.count > 0 && <span className="admin-chart-bar-value">{d.count}</span>}
            </div>
            <div className="admin-chart-bar-label">{d.date.slice(5)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InstrumentChart({ data }) {
  if (!data || data.length === 0) return null;
  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="admin-instrument-chart">
      {data.map(d => (
        <div key={d.instrument} className="admin-instrument-row">
          <span className="admin-instrument-name">{d.instrument}</span>
          <div className="admin-instrument-bar-bg">
            <div className="admin-instrument-bar" style={{ width: `${(d.count / maxCount) * 100}%` }} />
          </div>
          <span className="admin-instrument-count">{d.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const res = await admin.dashboard();
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Accès refusé. Admin requis.' : 'Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) return <div className="admin-loading">Chargement du dashboard...</div>;
  if (error) return <div className="admin-error">{error}</div>;
  if (!data) return null;

  const { users, sessions, trades, registrations_chart, top_instruments } = data;
  const winRate = trades.winning + trades.losing > 0
    ? ((trades.winning / (trades.winning + trades.losing)) * 100).toFixed(1)
    : '—';

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-header-right">
          <span className="admin-live-badge">
            <OnlineDot /> {users.online_count} en ligne
          </span>
          <button className="admin-refresh-btn" onClick={fetchData}>Actualiser</button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="admin-stats-grid">
        <StatCard label="Utilisateurs" value={users.total} sub={`+${users.today} aujourd'hui`} color="var(--accent)" />
        <StatCard label="En ligne" value={users.online_count} color="#22c55e" />
        <StatCard label="Sessions" value={sessions.total} sub={`${sessions.active} en cours`} color="var(--accent)" />
        <StatCard label="Trades" value={trades.total} sub={`+${trades.today} aujourd'hui`} />
        <StatCard label="Win Rate" value={winRate !== '—' ? `${winRate}%` : '—'} sub={`${trades.winning}W / ${trades.losing}L`} color={parseFloat(winRate) >= 50 ? '#22c55e' : '#ef4444'} />
        <StatCard label="Cette semaine" value={`+${users.this_week}`} sub="nouveaux users" color="#8b5cf6" />
      </div>

      {/* ── Tabs ── */}
      <div className="admin-tabs">
        {['overview', 'users', 'sessions'].map(t => (
          <button key={t} className={`admin-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'overview' ? 'Vue globale' : t === 'users' ? 'Utilisateurs' : 'Sessions'}
          </button>
        ))}
      </div>

      {/* ── Tab: Overview ── */}
      {tab === 'overview' && (
        <div className="admin-tab-content">
          <div className="admin-grid-2">
            <div className="admin-card">
              <h3>Inscriptions (30 derniers jours)</h3>
              <RegistrationChart data={registrations_chart} />
            </div>
            <div className="admin-card">
              <h3>Top instruments</h3>
              <InstrumentChart data={top_instruments} />
            </div>
          </div>

          <div className="admin-grid-2">
            <div className="admin-card">
              <h3>Utilisateurs en ligne</h3>
              {users.online.length === 0 ? (
                <p className="admin-empty">Aucun utilisateur en ligne</p>
              ) : (
                <div className="admin-online-list">
                  {users.online.map(u => (
                    <div key={u.id} className="admin-online-item">
                      <div className="admin-online-avatar">{u.username.charAt(0).toUpperCase()}</div>
                      <div className="admin-online-info">
                        <span className="admin-online-name">{u.username}</span>
                        <span className="admin-online-time">{timeAgo(u.last_activity)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="admin-card">
              <h3>Sessions par statut</h3>
              <div className="admin-session-breakdown">
                <div className="admin-breakdown-item">
                  <span className="admin-breakdown-dot" style={{ background: '#22c55e' }} />
                  <span>En cours</span>
                  <strong>{sessions.active}</strong>
                </div>
                <div className="admin-breakdown-item">
                  <span className="admin-breakdown-dot" style={{ background: 'var(--accent)' }} />
                  <span>Terminées</span>
                  <strong>{sessions.completed}</strong>
                </div>
                <div className="admin-breakdown-item">
                  <span className="admin-breakdown-dot" style={{ background: '#ef4444' }} />
                  <span>Abandonnées</span>
                  <strong>{sessions.abandoned}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Users ── */}
      {tab === 'users' && (
        <div className="admin-tab-content">
          <div className="admin-card">
            <h3>Tous les utilisateurs ({users.total})</h3>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Inscrit le</th>
                    <th>Dernière activité</th>
                    <th>Sessions</th>
                    <th>Trades</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {users.all.map(u => {
                    const isOnline = u.last_activity && (Date.now() - new Date(u.last_activity).getTime()) < 300000;
                    return (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>
                          <div className="admin-user-cell">
                            {isOnline && <OnlineDot />}
                            <strong>{u.username}</strong>
                          </div>
                        </td>
                        <td>{u.email}</td>
                        <td>{new Date(u.date_joined).toLocaleDateString('fr-FR')}</td>
                        <td>{timeAgo(u.last_activity)}</td>
                        <td>{u.sessions_count}</td>
                        <td>{u.trades_count}</td>
                        <td>
                          <span className={`admin-badge ${u.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                            {u.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Sessions ── */}
      {tab === 'sessions' && (
        <div className="admin-tab-content">
          <div className="admin-card">
            <h3>Sessions récentes</h3>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Utilisateur</th>
                    <th>Nom</th>
                    <th>Instrument</th>
                    <th>TF</th>
                    <th>Statut</th>
                    <th>Trades</th>
                    <th>Capital</th>
                    <th>P&L</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.recent.map(s => {
                    const pnl = s.final_capital ? s.final_capital - s.starting_capital : null;
                    return (
                      <tr key={s.id}>
                        <td>{s.id}</td>
                        <td><strong>{s.user}</strong></td>
                        <td>{s.name || '—'}</td>
                        <td>{s.instrument}</td>
                        <td>{s.timeframe}</td>
                        <td>
                          <span className={`admin-badge ${
                            s.status === 'in_progress' ? 'admin-badge-blue' :
                            s.status === 'completed' ? 'admin-badge-green' : 'admin-badge-red'
                          }`}>
                            {s.status === 'in_progress' ? 'En cours' : s.status === 'completed' ? 'Terminée' : 'Abandon'}
                          </span>
                        </td>
                        <td>{s.trades_count}</td>
                        <td>${s.starting_capital.toLocaleString()}</td>
                        <td style={{ color: pnl > 0 ? '#22c55e' : pnl < 0 ? '#ef4444' : 'inherit' }}>
                          {pnl !== null ? `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}` : '—'}
                        </td>
                        <td>{new Date(s.created_at).toLocaleDateString('fr-FR')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
